import { InfoPopover } from "@/components/cinch/InfoPopover";
import { KpiTile, tonePct } from "@/components/cinch/KpiTile";

import type { ForecastResponse } from "../../api";
import { fmtCur, fmtPct, monthLabel } from "../../format";

/** Forecast tile row for one renewal month — current or next. */
export function ForecastMonth({
  data,
  month,
  label,
}: {
  data: ForecastResponse;
  month: string | null | undefined;
  label: string;
}) {
  if (!month) return null;
  const row = (data.by_month || []).find((b) => b.month === month);
  if (!row) return null;

  const accts = (row.renew || 0) + (row.at_risk || 0) + (row.churn || 0) + (row.unknown || 0);
  const arrTotal = (row.renew_arr || 0) + (row.at_risk_arr || 0) + (row.churn_arr || 0) + (row.unknown_arr || 0);

  const renewalPct = accts > 0 ? ((row.renew + row.at_risk) / accts) * 100 : null;
  const renewArrPct = arrTotal > 0 ? ((row.renew_arr + row.at_risk_arr) / arrTotal) * 100 : null;
  const atRiskArrPct = arrTotal > 0 ? (row.at_risk_arr / arrTotal) * 100 : null;
  const renewOnlyPct = accts > 0 ? (row.renew / accts) * 100 : null;
  const renewOnlyArrPct = arrTotal > 0 ? (row.renew_arr / arrTotal) * 100 : null;

  const monthName = monthLabel(month + "-01");
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Forecast — {monthName}</h2>
        <InfoPopover title={`Forecast — ${monthName}`}>
          <p>The cohort of accounts with anniversaries in {monthName}, classified by current CSM forecast pick.</p>
          <p><b>Renewal %</b> = (Renew + Renewal At Risk) / total accounts in cohort. Unknown picks count in the denominator — uncalled accounts drag the % down until a CSM commits.</p>
          <p><b>Renew ARR %</b> = same, ARR-weighted.</p>
          <p><b>ARR at Risk</b> = ARR explicitly flagged Renewal At Risk by the CSM. "This much extra revenue could swing."</p>
          <p className="text-muted-foreground">{label}. Renewals occur on the 1st of {monthName}.</p>
        </InfoPopover>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        {label} · {accts} accounts · {fmtCur(arrTotal)} ARR
        {row.unknown > 0 && (
          <span className="ml-2 italic">({row.unknown} still Unknown)</span>
        )}
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiTile
          label="Renewal %"
          value={fmtPct(renewalPct)}
          tone={tonePct(renewalPct, 80, 60)}
          sub={`Renew only: ${fmtPct(renewOnlyPct)}`}
        />
        <KpiTile
          label="Renew ARR %"
          value={fmtPct(renewArrPct)}
          tone={tonePct(renewArrPct, 80, 60)}
          sub={`Renew only: ${fmtPct(renewOnlyArrPct)}`}
        />
        <KpiTile
          label="# Accounts"
          value={accts}
          sub={`${row.renew} renew · ${row.at_risk} at risk · ${row.churn} churn · ${row.unknown} unknown`}
        />
        <KpiTile
          label="ARR at risk"
          value={fmtCur(row.at_risk_arr)}
          tone={row.at_risk_arr > 0 ? "warning" : "neutral"}
          sub={atRiskArrPct != null ? `${fmtPct(atRiskArrPct)} of cohort ARR` : "—"}
        />
      </div>
    </section>
  );
}
